import React, { useState, useEffect, useCallback } from "react";
import { Modal, Table, Input, Typography, Spin, Image, Button, Popconfirm } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { dishesService } from "../../../../services/dishes-services/dishes.service";
import { DishType } from "../../../../constants/enums";
import { menuMenuService } from "../../../../services/menu-services/menuMenu.service";
import { menuMenuActions } from "../../../../store/slices/menu-slices";
import { DEFAULT_IMAGE } from "../../../../config";

const { Search } = Input;
const { Text } = Typography;

const PAGE_SIZE = 10;

const SelectSauceDishModal = ({
  isOpen,
  onClose,
  menuId,
  currentSauceDish,
  packId,
  mealId,
  dishId,
  onSaveOverride,
  onRemoveOverride,
}) => {
  const dispatch = useDispatch();
  const { i18n, t } = useTranslation();
  const currentLanguage = i18n.language;

  const [dishes, setDishes] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { dataOfDay } = useSelector((store) => store.availableDishes);

  const fetchDishes = useCallback(
    async (pageNum, word) => {
      setLoading(true);
      try {
        const data = await dishesService.getAllDishesPaginated({
          page: pageNum,
          word,
          type: DishType.SAUCE,
        });
        setDishes(data.data ?? data);
        setTotal(data.total ?? data.length ?? 0);
      } catch (err) {
        toast.error(t("Failed to load sauce dishes"));
      } finally {
        setLoading(false);
      }
    },
    [t],
  );

  useEffect(() => {
    if (isOpen) {
      setPage(1);
      setSearch("");
      fetchDishes(1, "");
    }
  }, [isOpen, fetchDishes]);

  const handleSearch = (value) => {
    setSearch(value);
    setPage(1);
    fetchDishes(1, value);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchDishes(newPage, search);
  };

  const handleRemoveDish = async () => {
    if (!menuId || saving) return;
    setSaving(true);
    try {
      if (onRemoveOverride) {
        await onRemoveOverride();
      } else {
        await menuMenuService.updateMenuPack(menuId, { sauceDishId: null, packId, mealId, dishId, date: dataOfDay });
        dispatch(menuMenuActions.changeTrigger());
      }
      toast.success(t("Sauce dish removed successfully"));
      onClose();
    } catch (err) {
      toast.error(t("Failed to remove sauce dish"));
    } finally {
      setSaving(false);
    }
  };

  const handleSelectDish = async (dish) => {
    if (!menuId || saving) return;
    setSaving(true);
    try {
      if (onSaveOverride) {
        await onSaveOverride(dish);
      } else {
        await menuMenuService.updateMenuPack(menuId, {
          sauceDishId: dish.id,
          packId,
          mealId,
          dishId,
          date: dataOfDay,
        });
        dispatch(menuMenuActions.changeTrigger());
      }
      toast.success(t("Sauce dish selected successfully"));
      onClose();
    } catch (err) {
      toast.error(t("Failed to assign sauce dish"));
    } finally {
      setSaving(false);
    }
  };

  const getDisplayName = (name) =>
    name?.[currentLanguage] || name?.["en"] || t("Unknown Dish");

  const columns = [
    {
      title: t("Image"),
      dataIndex: "img",
      key: "img",
      width: 72,
      render: (img) => (
        <Image
          width={100}
          height={100}
          src={img}
          alt="dish"
          style={{ objectFit: "cover", borderRadius: 6 }}
          fallback={DEFAULT_IMAGE}
        />
      ),
    },
    {
      title: t("Name"),
      dataIndex: "name",
      key: "name",
      render: (name) => getDisplayName(name),
    },
    {
      title: t("Energy"),
      dataIndex: "energy",
      key: "energy",
      width: 90,
      render: (v) => `${v ?? 0} kcal`,
    },
    {
      title: t("Proteins"),
      dataIndex: "proteins",
      key: "proteins",
      width: 80,
      render: (v) => `${v ?? 0} g`,
    },
    {
      title: t("Fats"),
      dataIndex: "fats",
      key: "fats",
      width: 70,
      render: (v) => `${v ?? 0} g`,
    },
    {
      title: t("Carbs"),
      dataIndex: "carbs",
      key: "carbs",
      width: 70,
      render: (v) => `${v ?? 0} g`,
    },
    {
      title: t("Weight"),
      dataIndex: "weight",
      key: "weight",
      width: 80,
      render: (v) => `${v ?? 0} g`,
    },
    {
      title: t("W.Netto"),
      dataIndex: "weightNetto",
      key: "weightNetto",
      width: 80,
      render: (v) => `${v ?? 0} g`,
    },
    {
      title: t("Cost"),
      dataIndex: "cost",
      key: "cost",
      width: 80,
    },
  ];

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      title={t("Select Sauce Dish")}
      footer={null}
      width={900}
      destroyOnClose
    >
      {currentSauceDish ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "12px 16px",
            marginBottom: 16,
            background: "#f6ffed",
            border: "1px solid #b7eb8f",
            borderRadius: 8,
          }}
        >
          <Image
            width={100}
            height={100}
            src={currentSauceDish.img}
            alt="current sauce dish"
            style={{ objectFit: "cover", borderRadius: 6 }}
            fallback={DEFAULT_IMAGE}
          />
          <div style={{ flex: 1 }}>
            <Text strong>{getDisplayName(currentSauceDish.name)}</Text>
            <div
              style={{
                display: "flex",
                gap: 16,
                marginTop: 4,
                flexWrap: "wrap",
              }}
            >
              <Text type="secondary">
                {t("Energy")}: {currentSauceDish.energy ?? 0} kcal
              </Text>
              <Text type="secondary">
                P: {currentSauceDish.proteins ?? 0} g
              </Text>
              <Text type="secondary">F: {currentSauceDish.fats ?? 0} g</Text>
              <Text type="secondary">C: {currentSauceDish.carbs ?? 0} g</Text>
              <Text type="secondary">
                {t("Weight")}: {currentSauceDish.weight ?? 0} g
              </Text>
              <Text type="secondary">
                W.N.: {currentSauceDish.weightNetto ?? 0} g
              </Text>
              <Text type="secondary">
                {t("Cost")}: {currentSauceDish.cost}
              </Text>
            </div>
          </div>
          <Popconfirm
            title={t("Remove sauce dish?")}
            description={t("This will unassign the sauce dish from this meal.")}
            onConfirm={handleRemoveDish}
            okText={t("Remove")}
            cancelText={t("Cancel")}
            okButtonProps={{ danger: true }}
          >
            <Button danger loading={saving}>{t("Remove")}</Button>
          </Popconfirm>
        </div>
      ) : (
        <div
          style={{
            padding: "12px 16px",
            marginBottom: 16,
            background: "#fffbe6",
            border: "1px solid #ffe58f",
            borderRadius: 8,
          }}
        >
          <Text type="warning">
            {t(
              "No sauce dish selected. Please select a Sauce Dish for this base dish.",
            )}
          </Text>
        </div>
      )}

      <Search
        placeholder={t("Search dishes...")}
        allowClear
        onSearch={handleSearch}
        style={{ marginBottom: 16 }}
      />

      <Spin spinning={saving}>
        <Table
          loading={loading}
          dataSource={dishes}
          columns={columns}
          rowKey="id"
          size="small"
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total,
            onChange: handlePageChange,
            showSizeChanger: false,
          }}
          onRow={(record) => ({
            onClick: () => handleSelectDish(record),
            style: { cursor: "pointer" },
          })}
        />
      </Spin>
    </Modal>
  );
};

export { SelectSauceDishModal };
